import { Link, Divider, Autocomplete, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, Outlet, Navigate } from "react-router-dom";
import APIRequest, { APIError } from "../../system/APIRequest";
import { setDocumentTitle } from "../../system/Utilities";
import { useTranslation } from "react-i18next";
import SidebarPageWrapper from '../../components/layout/SidebarPageWrapper';
import Sidebar from '../../components/layout/Sidebar';
import SidebarLink from '../../components/layout/SidebarLink';
import SidebarSection from '../../components/layout/SidebarSection';
import SidebarPage from '../../components/layout/SidebarPage';
import TopBar from "../../components/dashboardParts/TopBar";
import { permissionTest } from "system/InfegyUser";

export interface APIResource {
  name: string;
  endpoint: string;
  has_entries: boolean;
  sharable: boolean;
  permissions: {
    read: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  }
}

export default function AdminPage(props: {}): JSX.Element {
  const { endpoint } = useParams<any>();

  let navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const [apiResourceList, setApiResourceList] = useState<Array<APIResource>>([]);
  const [, setResourceListError] = useState<APIError | undefined>(undefined);
  const [selectedObject, setSelectedObject] = useState<APIResource | null>(null);

  useEffect(() => setDocumentTitle("Admin"), []);

  // Parse out the page name for the tab bar to highlight appropriately
  const page = (location.pathname.match(/\/admin\/([A-Za-z0-9\-_]+)/) || [null, null])[1];

  useEffect(() => {
    if (!page)
      navigate("resources");
  }, [navigate, page]);

  useEffect(() => {
    if (apiResourceList.length > 0)
      return;

    APIRequest("obj").then((response: any) => {
      let resources: Array<APIResource> = [];
      response['output'].forEach((obj: any) => {
        resources.push(obj);
      });

      resources.sort((a: APIResource, b: APIResource) => {
        return a.name.localeCompare(b.name);
      });

      setApiResourceList(resources);
    }).catch((reason: APIError) => {
      setResourceListError(reason);
    });
  }, [apiResourceList.length, endpoint, selectedObject]);


  if (!page) {
    return <Outlet />;
  }

  // First, validate we have permission to be here. If not, navigate back home
  if (!permissionTest("account_admin")) {
    return <Navigate to="/" />;
  }

  return (
    <SidebarPageWrapper>
      <Sidebar isOpen={true}>
        <Divider></Divider>
        <SidebarSection>{i18n.t("nav.resources")}</SidebarSection>
        <Autocomplete autoHighlight autoSelect
            onChange={(event: object, value: APIResource | null) => { 
              setSelectedObject(value); navigate(value ? "/admin/resources/" + value.endpoint : "/admin/resources"); 
            }}
            options={apiResourceList}
            value={selectedObject}
            getOptionLabel={(option: APIResource) => option.name}
            style={{ width: 300, marginLeft: "auto", marginRight: "auto" }}
            renderInput={(params: any) => {
              return (<TextField autoComplete="off" {...params} label={i18n.t("admin.resources.select-resource-placeholder")} variant="outlined" />);
            }} />
        {apiResourceList.map(resourceItem => 
          <SidebarLink key={resourceItem.endpoint}>
            <Link onClick={(event: React.SyntheticEvent<Element, Event>) => 
              { setSelectedObject(resourceItem); navigate(resourceItem ? "/admin/resources/" + resourceItem.endpoint : "/admin/resources"); }}>
              {resourceItem.name}
            </Link>
          </SidebarLink>
        )}
      </Sidebar>
      <SidebarPage>
        <TopBar></TopBar>
        <Outlet />
      </SidebarPage>
    </SidebarPageWrapper>
  );
}